import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ReactiveFormsModule, FormBuilder, Validators } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { switchMap } from 'rxjs/operators';
import { AuthService } from '../../../core/services/auth.service';
import { ModuleService } from '../../../core/services/module.service';

@Component({
    selector: 'app-login',
    standalone: true,
    imports: [CommonModule, ReactiveFormsModule, RouterLink],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss'
})
export class LoginComponent {
    private fb = inject(FormBuilder);
    private authService = inject(AuthService);
    private moduleService = inject(ModuleService);
    private router = inject(Router);

    loginForm = this.fb.group({
        email: ['', [Validators.required, Validators.email]],
        password: ['', [Validators.required]]
    });

    errorMessage = '';
    isLoading = false;

    onSubmit() {
        if (this.isLoading) return; // Prevent double submission

        const email = this.loginForm.get('email')?.value?.trim();
        const password = this.loginForm.get('password')?.value?.trim();

        // Basic validation
        if (!email || !this.isValidEmail(email)) {
            this.errorMessage = 'Please enter a valid email address.';
            return;
        }

        if (!password) {
            this.errorMessage = 'Please enter your password.';
            return;
        }

        this.isLoading = true;
        this.errorMessage = '';
        this.authService.login({ email, password })
            .pipe(switchMap(() => this.moduleService.refreshPermissions()))
            .subscribe({
                next: () => {
                    this.isLoading = false;
                    const role = this.authService.getUserRole();
                    if (role === 'Staff') {
                        this.router.navigate(['/reports/timelogs']);
                    } else if (role === 'SystemAdmin' || role === 'Admin') {
                        this.router.navigate(['/reports']);
                    } else if (role === 'Finance') {
                        this.router.navigate(['/finance-report']);
                    } else {
                        this.router.navigate(['/profile']);
                    }
                },
                error: (err) => {
                    this.isLoading = false;
                    this.errorMessage = 'Invalid credentials or login failed.';
                    console.error(err);
                }
            });
    }

    private isValidEmail(email: string): boolean {
        const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailPattern.test(email);
    }
}
